import { deleteDoc, deleteField, getDocs, limit, orderBy, query, setDoc, updateDoc, where, } from "firebase/firestore";
import { asCategoryId, between, jitter, err, newId, ok, } from "@pack-it-up/shared";
import { toCategoryDto, toDomainCategory } from "../mappers/index.js";
import { observeQuery, patchField, toDomainError } from "./support.js";
export const makeCategoryRepo = (db, p) => {
    /**
     * Compute the `order` for a new category appended at the end under `parentId`.
     *
     * `order` is a **fractional index** (Doc 02 §6): an opaque, lexicographically
     * sortable string, NOT a 0,1,2 position. Categories under a parent are read/sorted
     * by it; to append we read the current last sibling and pick a key after it (one
     * indexed read, one row written — siblings are never renumbered). Reorders later
     * pass an explicit destination key via `reorderCategory`.
     */
    const appendOrder = async (parentId) => {
        const snap = await getDocs(query(p.categories(), where("parentId", "==", parentId), orderBy("order", "desc"), limit(1)));
        const last = snap.docs[0]?.get("order");
        return jitter(between((last ?? null), null));
    };
    return {
        observeCategories: (parentId) => observeQuery(query(p.categories(), where("parentId", "==", parentId), orderBy("order")), toDomainCategory, "categories"),
        async createCategory(input) {
            try {
                const id = newId();
                const cat = {
                    id: asCategoryId(id),
                    parentId: input.parentId,
                    name: input.name,
                    order: await appendOrder(input.parentId),
                    collapsedBy: {},
                    ...(input.emoji !== undefined ? { emoji: input.emoji } : {}),
                    ...(input.kind !== undefined ? { kind: input.kind } : {}),
                };
                await setDoc(p.category(id), toCategoryDto(cat));
                return ok(asCategoryId(id));
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async updateCategory(patch) {
            const out = {};
            patchField(out, "name", patch.name);
            patchField(out, "order", patch.order);
            patchField(out, "emoji", patch.emoji);
            try {
                await updateDoc(p.category(patch.categoryId), out);
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Per-person collapse (Issue #240): one nested field path, so two people collapsing
        // different (or the same) category never write over each other. Expanding deletes the
        // entry rather than storing `false`, keeping the map to just the people who collapsed.
        async setCollapsed(categoryId, userId, collapsed) {
            try {
                const path = `collapsedBy.${userId}`;
                await updateDoc(p.category(categoryId), { [path]: collapsed ? true : deleteField() });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async reorderCategory(categoryId, order) {
            try {
                await updateDoc(p.category(categoryId), { order });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async deleteCategory(categoryId) {
            try {
                await deleteDoc(p.category(categoryId));
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
    };
};
//# sourceMappingURL=category.repo.js.map